/**
 * The bar above the paper.
 *
 * On the phone all controls move behind the burger: whoever writes needs no
 * toolbar — the paper shall have the space. On a wide screen the group
 * dissolves (`display: contents`) and the buttons stand in the bar as before.
 *
 * The save state stays visible in both cases. Whether the text is safe is
 * nothing for which one shall open a menu.
 */

import { useRef } from 'preact/hooks'
import { Icon } from './icons.js'
import { useDismiss } from './hooks.js'
import * as st from '../state.js'
import type { Doc } from '../types.js'
import { DocsPanel } from './DocsPanel.js'
import { CheckPanel } from './CheckPanel.js'
import { PaperPanel } from './PaperPanel.js'
import { PhotoPanel } from './PhotoPanel.js'

export function Bar({
  onPrint, onPick, onNew, onTakeOver, onRetry,
}: {
  onPrint: () => void
  onPick: (d: Doc) => void
  onNew: () => void
  onTakeOver: (text: string) => void
  onRetry: () => void
}) {
  const docsBtn = useRef<HTMLButtonElement>(null)
  const paperBtn = useRef<HTMLButtonElement>(null)
  const checkBtn = useRef<HTMLButtonElement>(null)
  const burgerBtn = useRef<HTMLButtonElement>(null)
  const tools = useRef<HTMLDivElement>(null)

  useDismiss(st.burger.value, () => (st.burger.value = false), tools, burgerBtn)

  const toggle = (p: st.Panel): void => {
    st.panel.value = st.panel.value === p ? null : p
  }

  return (
    <>
      <header class="bar">
        {/* The way back, written the same as on every other screen.
        
            The logo did this job before, and it does go back — but nothing
            about a wordmark says so, and this was the one screen where the
            way out had to be guessed. The logo is gone from here: two things
            at the top left that both lead to the same place is one too many,
            and the one that says where it goes is the one to keep. */}
        <button
          type="button"
          class="page-back"
          onClick={() => st.goTo('start')}
        >
          <Icon name="chevron-left" size={17} />
          Übersicht
        </button>
        <SaveState />
        <span class="spacer" />

        <button
          ref={burgerBtn}
          class="btn burger"
          aria-haspopup="true"
          aria-expanded={st.burger.value}
          aria-label="Menü"
          onClick={() => (st.burger.value = !st.burger.value)}
        >
          <Icon name="menu" />
        </button>

        <div ref={tools} class={`bar-tools${st.burger.value ? ' open' : ''}`}>
          <button
            ref={docsBtn}
            class="btn"
            aria-haspopup="true"
            aria-expanded={st.panel.value === 'docs'}
            onClick={() => toggle('docs')}
          >
            <Icon name="file-text" />
            Texte
            <Icon name="chevron-down" size={14} />
          </button>

          <button
            ref={paperBtn}
            class="btn"
            title="Papier und Druck"
            aria-haspopup="true"
            aria-expanded={st.panel.value === 'paper'}
            onClick={() => toggle('paper')}
          >
            <Icon name="layout-grid" />
            Papier
            <Icon name="chevron-down" size={14} />
          </button>

          <button
            ref={checkBtn}
            class={`btn${st.checking.value ? '' : ' muted'}`}
            title="Was geprüft wird"
            aria-haspopup="true"
            aria-expanded={st.panel.value === 'check'}
            onClick={() => toggle('check')}
          >
            <Icon name="spell-check" />
            <span class="check-label">{checkLabel()}</span>
            <Icon name="chevron-down" size={14} />
          </button>

          <button class="btn" onClick={onPrint}>
            <Icon name="printer" />
            Drucken
          </button>
        </div>
      </header>

      <DocsPanel anchor={docsBtn} onPick={onPick} onNew={onNew} />
      <PaperPanel anchor={paperBtn} />
      <CheckPanel anchor={checkBtn} />
      {/* The result of a photograph, in the middle of the screen: it answers
          something that happened on another screen, so it hangs off no
          button. */}
      <PhotoPanel onTakeOver={onTakeOver} onRetry={onRetry} />
    </>
  )
}

/**
 * What stands on the button when something is switched off.
 *
 * Otherwise one would have to open the menu to see that no check is done.
 */
function checkLabel(): string {
  if (!st.checking.value) return 'Prüfung: aus'
  if (!st.commas.value) return 'Prüfung: ohne Kommas'
  return 'Prüfung'
}

/**
 * The save state.
 *
 * The tick says "saved" more quickly than the word next to it.
 */
function SaveState() {
  const d = st.doc.value
  if (!d) {
    return <span class={`save-state${st.dirty.value ? ' dirty' : ''}`} role="status" aria-live="polite" />
  }
  return (
    <span
      class={`save-state${st.dirty.value ? ' dirty' : ' saved'}`}
      role="status"
      aria-live="polite"
    >
      {st.dirty.value ? (
        'Speichert …'
      ) : (
        <>
          <Icon name="circle-check" size={13} />
          <span>{d.title || 'Gespeichert'}</span>
        </>
      )}
    </span>
  )
}
